import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { subirRoteiroLote } from '../lib/portal';
import { classificarConteudo, TAXONOMIA_PADRAO_ADVOCACIA } from '../lib/classificarConteudo';

const FORMATOS = ['Criativo', 'Reels', 'Vídeo TikTok', 'Carrossel', 'Post'];
const PLATAFORMAS = ['Instagram', 'TikTok'];
const HORIZONTE_DIAS_ACERVO = 14;

function hoje() { return new Date().toISOString().split('T')[0]; }
function itemVazio() { return { titulo: '', formato: 'Reels', plataforma: 'Instagram' }; }

// TikTok se auto-agenda (1 ou 2/dia); Criativo o cliente escolhe até
// 10/mês pra gravar; Carrossel/Post/Reels do Instagram o cliente
// escolhe até 2/semana (a Vuli produz, não o cliente).
function grupoConteudo(it) {
  if (it.formato === 'Vídeo TikTok' && it.plataforma === 'TikTok') return 'tiktok';
  if (it.formato === 'Criativo') return 'criativo';
  return 'instagram';
}

// Distribui datas pros vídeos de TikTok respeitando o ritmo (itens/dia),
// pulando dias que já estão ocupados. Preenche só um horizonte de ~2
// semanas — o resto fica sem data (acervo), sendo puxado aos poucos
// depois pela tela de Conteúdo.
function distribuirDatasTikTok(qtdItens, ritmoPorDia, ocupacaoExistente) {
  const ocupacao = { ...ocupacaoExistente };
  const datas = [];
  const cursor = new Date();
  cursor.setDate(cursor.getDate() + 1); // começa amanhã
  let diasChecados = 0;
  while (datas.length < qtdItens && diasChecados < HORIZONTE_DIAS_ACERVO) {
    const dataStr = cursor.toISOString().split('T')[0];
    const usado = ocupacao[dataStr] || 0;
    if (usado < ritmoPorDia) {
      datas.push(dataStr);
      ocupacao[dataStr] = usado + 1;
    } else {
      cursor.setDate(cursor.getDate() + 1);
      diasChecados++;
    }
  }
  return datas;
}

export default function RoteirosCliente() {
  const { projetoId } = useParams();
  const navigate = useNavigate();

  const [projeto, setProjeto] = useState(null);
  const [lotes, setLotes] = useState([]);
  const [conteudos, setConteudos] = useState([]);
  const [eventosPorConteudo, setEventosPorConteudo] = useState({});
  const [taxonomia, setTaxonomia] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [novoTema, setNovoTema] = useState({ subnicho: '', assunto: '', palavras_chave: '' });

  const [tituloLote, setTituloLote] = useState('');
  const [explicacaoLote, setExplicacaoLote] = useState('');
  const [linkDrive, setLinkDrive] = useState('');
  const [arquivoLote, setArquivoLote] = useState(null);
  const [itens, setItens] = useState([itemVazio()]);
  const [enviando, setEnviando] = useState(false);
  const [progresso, setProgresso] = useState('');
  const [ritmoTiktok, setRitmoTiktok] = useState(1);

  useEffect(() => { carregarTudo(); }, [projetoId]);

  async function carregarTudo() {
    setCarregando(true);
    const [{ data: p }, { data: ls }, { data: cts }, { data: tax }] = await Promise.all([
      supabase.from('projetos').select('id, nome, cor, ritmo_tiktok_dia, link_drive_cliente').eq('id', projetoId).single(),
      supabase.from('lotes_roteiro').select('*').eq('projeto_id', projetoId).order('created_at', { ascending: false }),
      supabase.from('conteudos').select('*').eq('projeto_id', projetoId).not('lote_id', 'is', null).order('data'),
      supabase.from('taxonomia_conteudo').select('*').eq('projeto_id', projetoId).order('subnicho'),
    ]);
    setProjeto(p || null);
    setRitmoTiktok(p?.ritmo_tiktok_dia || 1);
    setLotes(ls || []);
    setConteudos(cts || []);
    setTaxonomia(tax || []);

    const ids = (cts || []).map(c => c.id);
    if (ids.length) {
      const { data: evs } = await supabase.from('roteiro_eventos').select('*').in('conteudo_id', ids);
      const mapa = {};
      (evs || []).forEach(e => { (mapa[e.conteudo_id] ||= []).push(e); });
      setEventosPorConteudo(mapa);
    }
    setCarregando(false);
  }

  async function salvarLinkDriveCliente(valor) {
    const { data, error } = await supabase.from('projetos').update({ link_drive_cliente: valor || null }).eq('id', projetoId).select().single();
    if (!error && data) setProjeto(data);
  }

  function atualizarItem(i, campo, valor) {
    setItens(itens.map((it, idx) => idx === i ? { ...it, [campo]: valor } : it));
  }
  function adicionarItem() { setItens([...itens, itemVazio()]); }
  function removerItem(i) { setItens(itens.filter((_, idx) => idx !== i)); }

  async function enviarLote() {
    if (!tituloLote.trim()) { alert('Dá um título pra essa leva.'); return; }
    if (!itens.some(it => it.titulo.trim())) { alert('Adiciona pelo menos um roteiro com título.'); return; }

    setEnviando(true);
    try {
      if (Number(ritmoTiktok) !== Number(projeto?.ritmo_tiktok_dia || 1)) {
        await supabase.from('projetos').update({ ritmo_tiktok_dia: ritmoTiktok }).eq('id', projetoId);
      }

      const { data: lote, error: erroLote } = await supabase.from('lotes_roteiro')
        .insert([{ projeto_id: projetoId, titulo: tituloLote, explicacao: explicacaoLote || null, link_drive_upload: linkDrive || null }])
        .select().single();
      if (erroLote) throw erroLote;

      if (arquivoLote) {
        setProgresso('Enviando o .docx...');
        const path = await subirRoteiroLote(projetoId, lote.id, arquivoLote);
        await supabase.from('lotes_roteiro').update({ arquivo_unico: path }).eq('id', lote.id);
      }

      const validos = itens.filter(it => it.titulo.trim());

      // Datas pros vídeos de TikTok, evitando colidir com o que já está agendado
      const qtdTiktok = validos.filter(it => grupoConteudo(it) === 'tiktok').length;
      let datasTiktok = [];
      if (qtdTiktok > 0) {
        const amanha = new Date(); amanha.setDate(amanha.getDate() + 1);
        const { data: existentes } = await supabase.from('conteudos')
          .select('data').eq('projeto_id', projetoId).eq('plataforma', 'TikTok').eq('formato', 'Vídeo TikTok')
          .not('data', 'is', null).gte('data', amanha.toISOString().split('T')[0]);
        const ocupacao = {};
        (existentes || []).forEach(c => { ocupacao[c.data] = (ocupacao[c.data] || 0) + 1; });
        datasTiktok = distribuirDatasTikTok(qtdTiktok, ritmoTiktok, ocupacao);
      }

      let tiktokUsados = 0;
      for (let i = 0; i < validos.length; i++) {
        const it = validos[i];
        setProgresso(`Cadastrando ${i + 1} de ${validos.length}...`);
        const grupo = grupoConteudo(it);
        const { subnicho, assunto } = classificarConteudo(it.titulo, taxonomia);
        let data = null;
        if (grupo === 'tiktok') { data = datasTiktok[tiktokUsados] || null; tiktokUsados++; }
        const { error: erroConteudo } = await supabase.from('conteudos')
          .insert([{
            projeto_id: projetoId, lote_id: lote.id, titulo: it.titulo, formato: it.formato,
            plataforma: it.plataforma, subnicho, assunto, data,
            requer_gravacao_cliente: grupo !== 'instagram', status: 'Roteiro pronto',
          }]);
        if (erroConteudo) throw erroConteudo;
      }

      setTituloLote(''); setExplicacaoLote(''); setLinkDrive(''); setArquivoLote(null); setItens([itemVazio()]);
      await carregarTudo();
    } catch (e) {
      alert('Erro ao enviar a leva: ' + e.message);
    }
    setEnviando(false); setProgresso('');
  }

  async function excluirItem(item) {
    if (!window.confirm(`Excluir o roteiro "${item.titulo}"?`)) return;
    await supabase.from('conteudos').delete().eq('id', item.id);
    carregarTudo();
  }

  async function adicionarTema() {
    if (!novoTema.subnicho.trim() || !novoTema.assunto.trim()) { alert('Preenche subnicho e assunto.'); return; }
    const palavras_chave = novoTema.palavras_chave.split(',').map(p => p.trim()).filter(Boolean);
    if (!palavras_chave.length) { alert('Adiciona pelo menos uma palavra-chave (separadas por vírgula) pra o sistema reconhecer.'); return; }
    const { data, error } = await supabase.from('taxonomia_conteudo')
      .insert([{ projeto_id: projetoId, subnicho: novoTema.subnicho, assunto: novoTema.assunto, palavras_chave }])
      .select().single();
    if (error) { alert('Erro: ' + error.message); return; }
    setTaxonomia([...taxonomia, data]);
    setNovoTema({ subnicho: '', assunto: '', palavras_chave: '' });
  }

  async function excluirTema(tema) {
    await supabase.from('taxonomia_conteudo').delete().eq('id', tema.id);
    setTaxonomia(taxonomia.filter(t => t.id !== tema.id));
  }

  async function usarTaxonomiaPadrao() {
    const payload = TAXONOMIA_PADRAO_ADVOCACIA.map(t => ({ ...t, projeto_id: projetoId }));
    const { data, error } = await supabase.from('taxonomia_conteudo').insert(payload).select();
    if (error) { alert('Erro: ' + error.message); return; }
    setTaxonomia([...taxonomia, ...data]);
  }

  async function excluirLote(lote) {
    if (!window.confirm(`Excluir a leva "${lote.titulo}" e todos os roteiros dela?`)) return;
    await supabase.from('conteudos').delete().eq('lote_id', lote.id);
    await supabase.from('lotes_roteiro').delete().eq('id', lote.id);
    carregarTudo();
  }

  function statusRoteiro(conteudoId) {
    const evs = eventosPorConteudo[conteudoId] || [];
    const gravado = evs.find(e => e.etapa === 'gravado');
    const enviado = evs.find(e => e.etapa === 'enviado_edicao');
    const recusado = evs.find(e => e.etapa === 'recusado');
    if (recusado) return { label: `🚫 Recusado pelo cliente${recusado.detalhe ? ' — ' + recusado.detalhe : ''}`, cor: '#dc2626' };
    if (enviado) return { label: '📤 Enviado pra edição', cor: '#16a34a' };
    if (gravado) return { label: '🎥 Gravado', cor: '#C9A84C' };
    return { label: '⏳ Aguardando gravação', cor: 'var(--gray-400)' };
  }

  if (carregando) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>Carregando...</div>;
  if (!projeto) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>Projeto não encontrado.</div>;

  return (
    <div>
      <div className="section-header">
        <button onClick={() => navigate('/projetos')} style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12, color: 'var(--text-secondary)' }}>← Projetos</button>
        <h2 className="section-title">Roteiros do Cliente — {projeto.nome}</h2>
        <div className="section-line" />
      </div>

      {/* Link do Drive fixo do projeto — sempre visível no topo do portal */}
      <div className="card" style={{ padding: 16, marginBottom: 20, background: '#FAF8FF' }}>
        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--gray-400)', marginBottom: 4 }}>
          📁 Link do Drive pra o cliente enviar as gravações <span style={{ fontWeight: 400 }}>(fixo — aparece sempre na aba Conteúdo do portal, não depende de leva)</span>
        </label>
        <input defaultValue={projeto.link_drive_cliente || ''} onBlur={e => salvarLinkDriveCliente(e.target.value.trim())}
          placeholder="https://drive.google.com/..."
          style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: 'white', boxSizing: 'border-box' }} />
      </div>

      {/* Nova leva */}
      <div className="card" style={{ padding: 20, marginBottom: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 14, textTransform: 'uppercase' }}>Nova leva de roteiros</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--gray-400)', marginBottom: 4 }}>Título da leva</label>
            <input value={tituloLote} onChange={e => setTituloLote(e.target.value)} placeholder="Ex: Roteiros de julho — semana 1"
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: 'var(--bg-secondary)', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--gray-400)', marginBottom: 4 }}>Link do Drive (pra ele subir a gravação)</label>
            <input value={linkDrive} onChange={e => setLinkDrive(e.target.value)} placeholder="https://drive.google.com/..."
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: 'var(--bg-secondary)', boxSizing: 'border-box' }} />
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--gray-400)', marginBottom: 4 }}>Explicação (o que é essa leva, orientações gerais)</label>
          <textarea value={explicacaoLote} onChange={e => setExplicacaoLote(e.target.value)} rows={3}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: 'var(--bg-secondary)', boxSizing: 'border-box', resize: 'vertical' }} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--gray-400)', marginBottom: 4 }}>O .docx com todos os roteiros dessa leva (um arquivo só, dividido por dentro)</label>
          <label style={{ display: 'inline-block', padding: '9px 16px', border: '1px dashed var(--border)', borderRadius: 8, fontSize: 12.5, background: arquivoLote ? '#dcfce7' : 'var(--bg-secondary)', cursor: 'pointer', color: arquivoLote ? '#16a34a' : 'var(--text-secondary)' }}>
            {arquivoLote ? `✓ ${arquivoLote.name}` : '⬆ Escolher .docx'}
            <input type="file" accept=".docx" style={{ display: 'none' }} onChange={e => setArquivoLote(e.target.files?.[0] || null)} />
          </label>
        </div>

        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4, textTransform: 'uppercase' }}>Roteiros desta leva (cada um vira um item com check próprio pro cliente)</div>
        <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 10 }}>O tema (subnicho/assunto) é reconhecido automaticamente pelo título — não precisa digitar.</div>
        {itens.map((it, i) => {
          const classificado = classificarConteudo(it.titulo, taxonomia);
          return (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, marginBottom: 4, alignItems: 'center' }}>
              <input value={it.titulo} onChange={e => atualizarItem(i, 'titulo', e.target.value)} placeholder="Título/tema do roteiro"
                style={{ padding: '7px 9px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12.5, background: 'var(--bg-secondary)' }} />
              <select value={it.formato} onChange={e => atualizarItem(i, 'formato', e.target.value)}
                style={{ padding: '7px 9px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12.5, background: 'var(--bg-secondary)' }}>
                {FORMATOS.map(f => <option key={f}>{f}</option>)}
              </select>
              <select value={it.plataforma} onChange={e => atualizarItem(i, 'plataforma', e.target.value)}
                style={{ padding: '7px 9px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12.5, background: 'var(--bg-secondary)' }}>
                {PLATAFORMAS.map(p => <option key={p}>{p}</option>)}
              </select>
              <button onClick={() => removerItem(i)} style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 16 }}>×</button>
              {it.titulo.trim() && (
                <div style={{ gridColumn: '1 / -1', fontSize: 11, color: 'var(--purple-rich)', marginBottom: 6 }}>
                  🏷 {classificado.subnicho} › {classificado.assunto}
                </div>
              )}
            </div>
          );
        })}
        <button onClick={adicionarItem} style={{ background: 'transparent', border: '1px dashed var(--border)', color: 'var(--purple-rich)', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, marginBottom: 16 }}>
          + adicionar outro roteiro
        </button>

        {(() => {
          const contagem = { tiktok: 0, criativo: 0, instagram: 0 };
          itens.filter(it => it.titulo.trim()).forEach(it => { contagem[grupoConteudo(it)]++; });
          const total = contagem.tiktok + contagem.criativo + contagem.instagram;
          if (total === 0) return null;
          return (
            <div style={{ background: 'var(--bg-secondary)', borderRadius: 10, padding: '12px 16px', marginBottom: 18, fontSize: 12 }}>
              <div style={{ fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, textTransform: 'uppercase', fontSize: 10.5 }}>Como vai ficar organizado</div>
              {contagem.tiktok > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                  <span>🎵 <strong>{contagem.tiktok} de TikTok</strong> — vão se distribuir sozinhos no calendário, no ritmo de</span>
                  <select value={ritmoTiktok} onChange={e => setRitmoTiktok(Number(e.target.value))}
                    style={{ padding: '3px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, background: 'white' }}>
                    <option value={1}>1 por dia</option>
                    <option value={2}>2 por dia</option>
                  </select>
                  <span>— o resto fica no acervo, puxado aos poucos.</span>
                </div>
              )}
              {contagem.criativo > 0 && (
                <div style={{ marginBottom: 8 }}>🎬 <strong>{contagem.criativo} criativo(s)</strong> — vão pro pool "A gravar" no portal; o cliente escolhe até 10 por mês pra gravar.</div>
              )}
              {contagem.instagram > 0 && (
                <div>📷 <strong>{contagem.instagram} de Instagram</strong> (carrossel/post/reels) — vão pro pool; o cliente escolhe até 2 por semana pra produzirmos.</div>
              )}
            </div>
          );
        })()}

        <div>
          <button onClick={enviarLote} disabled={enviando}
            style={{ background: 'linear-gradient(135deg,#3F0051,#7B2FBE)', color: 'white', border: 'none', padding: '11px 28px', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
            {enviando ? (progresso || 'Enviando...') : 'Criar leva e enviar'}
          </button>
        </div>
      </div>

      {/* Temas (taxonomia) */}
      <div className="card" style={{ padding: 20, marginBottom: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase' }}>Temas deste projeto</div>
        <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 14 }}>É daqui que o sistema reconhece o subnicho/assunto pelo título — ensina uma vez, usa sempre.</div>

        {taxonomia.length === 0 && (
          <button onClick={usarTaxonomiaPadrao} style={{ background: 'var(--bg-secondary)', border: '1px dashed var(--border)', color: 'var(--purple-rich)', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12, marginBottom: 14 }}>
            Começar com a taxonomia padrão de advocacia/dívidas
          </button>
        )}

        {taxonomia.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
            {taxonomia.map(t => (
              <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', background: 'var(--bg-secondary)', borderRadius: 6 }}>
                <span style={{ fontSize: 12.5, color: 'var(--text-primary)' }}>
                  <strong>{t.subnicho}</strong> › {t.assunto}
                  <span style={{ color: 'var(--gray-400)' }}> · {(t.palavras_chave || []).join(', ')}</span>
                </span>
                <button onClick={() => excluirTema(t)} style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 11 }}>excluir</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.4fr auto', gap: 8, alignItems: 'center' }}>
          <input value={novoTema.subnicho} onChange={e => setNovoTema({ ...novoTema, subnicho: e.target.value })} placeholder="Subnicho (ex: Revisional)"
            style={{ padding: '7px 9px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12.5, background: 'var(--bg-secondary)' }} />
          <input value={novoTema.assunto} onChange={e => setNovoTema({ ...novoTema, assunto: e.target.value })} placeholder="Assunto (ex: Revisional de veículo)"
            style={{ padding: '7px 9px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12.5, background: 'var(--bg-secondary)' }} />
          <input value={novoTema.palavras_chave} onChange={e => setNovoTema({ ...novoTema, palavras_chave: e.target.value })} placeholder="Palavras-chave, separadas por vírgula"
            style={{ padding: '7px 9px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12.5, background: 'var(--bg-secondary)' }} />
          <button onClick={adicionarTema} style={{ background: 'var(--purple)', color: 'white', border: 'none', padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>+ tema</button>
        </div>
      </div>

      {/* Lotes existentes */}
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 12, textTransform: 'uppercase' }}>Levas já enviadas</div>
      {lotes.length === 0 && conteudos.length === 0 && <div className="card" style={{ padding: 30, textAlign: 'center', color: 'var(--gray-400)' }}>Nenhuma leva ainda.</div>}
      {(() => {
        const idsLotesValidos = new Set(lotes.map(l => l.id));
        const orfaos = conteudos.filter(c => !c.lote_id || !idsLotesValidos.has(c.lote_id));
        if (!orfaos.length) return null;
        return (
          <div className="card" style={{ padding: 18, marginBottom: 14, border: '1.5px solid #fde68a', background: '#fffbeb' }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#92400e', marginBottom: 4 }}>⚠ Roteiros sem leva ({orfaos.length})</div>
            <div style={{ fontSize: 11.5, color: '#92400e', marginBottom: 10 }}>
              Esses roteiros apontam pra uma leva que não existe mais no banco (provavelmente por causa da instabilidade do Supabase) — continuam aqui pra você não perder o controle deles.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {orfaos.map(c => {
                const st = statusRoteiro(c.id);
                return (
                  <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', background: 'white', borderRadius: 6 }}>
                    <span style={{ fontSize: 12.5, color: 'var(--text-primary)' }}>{c.titulo} <span style={{ color: 'var(--gray-400)' }}>· {c.formato} · {c.plataforma}{c.assunto ? ` · 🏷 ${c.assunto}` : ''}</span></span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: st.cor }}>{st.label}</span>
                      <button onClick={() => excluirItem(c)} style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 11 }}>excluir</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
      {lotes.map(lote => {
        const itensDoLote = conteudos.filter(c => c.lote_id === lote.id);
        return (
          <div key={lote.id} className="card" style={{ padding: 18, marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{lote.titulo}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{new Date(lote.created_at).toLocaleDateString('pt-BR')} · {itensDoLote.length} roteiro(s)</div>
              </div>
              <button onClick={() => excluirLote(lote)} style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 11 }}>excluir leva</button>
            </div>
            {lote.explicacao && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 10, whiteSpace: 'pre-wrap' }}>{lote.explicacao}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {itensDoLote.map(c => {
                const st = statusRoteiro(c.id);
                return (
                  <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', background: 'var(--bg-secondary)', borderRadius: 6 }}>
                    <span style={{ fontSize: 12.5, color: 'var(--text-primary)' }}>{c.titulo} <span style={{ color: 'var(--gray-400)' }}>· {c.formato} · {c.plataforma}{c.assunto ? ` · 🏷 ${c.assunto}` : ''}</span></span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: st.cor }}>{st.label}</span>
                      <button onClick={() => excluirItem(c)} style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 11 }}>excluir</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
